import React from 'react';

/**
 * Renders a vitals template's fields as an input form. Shared by the clinic
 * profile and the patient portal so a template always looks and validates the
 * same on both sides.
 */
const inputCls =
  'mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400';

const outOfRange = (field, value) => {
  if (field.type !== 'number' || value === '' || value === undefined || value === null) return false;
  const num = Number(value);
  if (Number.isNaN(num)) return false;
  if (field.normalLow !== null && field.normalLow !== undefined && num < field.normalLow) return true;
  if (field.normalHigh !== null && field.normalHigh !== undefined && num > field.normalHigh) return true;
  return false;
};

export const normalRangeText = (field) => {
  const { normalLow: low, normalHigh: high, unit } = field;
  const suffix = unit ? ` ${unit}` : '';
  if (low !== null && low !== undefined && high !== null && high !== undefined) return `${low}–${high}${suffix}`;
  if (low !== null && low !== undefined) return `≥ ${low}${suffix}`;
  if (high !== null && high !== undefined) return `≤ ${high}${suffix}`;
  return '';
};

export default function VitalsDynamicForm({ template, values, onChange, disabled = false, columns = 2 }) {
  if (!template) return null;

  const set = (key, value) => onChange({ ...values, [key]: value });

  return (
    <div className={`grid grid-cols-1 gap-4 ${columns === 2 ? 'md:grid-cols-2' : ''}`}>
      {(template.fields || []).map((field) => {
        const value = values?.[field.key] ?? '';
        const flagged = outOfRange(field, value);
        const range = normalRangeText(field);

        return (
          <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
            <label className="block text-sm font-medium text-slate-700">
              {field.label}
              {field.unit && <span className="ml-1 text-xs font-normal text-slate-500">({field.unit})</span>}
              {field.required && <span className="ml-0.5 text-rose-500">*</span>}
            </label>

            {field.type === 'select' ? (
              <select className={inputCls} value={value} disabled={disabled} onChange={(e) => set(field.key, e.target.value)}>
                <option value="">Select…</option>
                {(field.options || []).map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : field.type === 'boolean' ? (
              <div className="mt-2 flex gap-4">
                {['Yes', 'No'].map((opt) => (
                  <label key={opt} className="flex items-center gap-1.5 text-sm text-slate-700">
                    <input
                      type="radio"
                      name={field.key}
                      disabled={disabled}
                      checked={String(value) === String(opt === 'Yes')}
                      onChange={() => set(field.key, opt === 'Yes')}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            ) : field.type === 'textarea' ? (
              <textarea
                rows={3}
                className={inputCls}
                value={value}
                disabled={disabled}
                onChange={(e) => set(field.key, e.target.value)}
              />
            ) : (
              <input
                type={field.type === 'number' ? 'number' : field.type}
                step="any"
                min={field.min ?? undefined}
                max={field.max ?? undefined}
                className={`${inputCls} ${flagged ? 'border-amber-400 bg-amber-50' : ''}`}
                value={value}
                disabled={disabled}
                onChange={(e) => set(field.key, e.target.value)}
              />
            )}

            <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs">
              {range && <span className="text-slate-500">Normal: {range}</span>}
              {/* Wording is deliberately neutral: a patient seeing this must not
                  read it as a diagnosis. */}
              {flagged && <span className="font-medium text-amber-700">Outside the usual range</span>}
              {field.helpText && <span className="w-full text-slate-500">{field.helpText}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
